import React, { useState } from 'react';

const Contact = ({ showToast }) => {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // Validations
    const name = formData.get('name');
    const email = formData.get('email');
    const phone = formData.get('phone').replace(/[^0-9]/g, '');

    if (name.length < 3) {
      showToast('Please enter a valid name.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast('Please enter a valid email.');
      return;
    }
    if (phone.length < 10) {
      showToast('Please enter a valid 10-digit phone number.');
      return;
    }

    setLoading(true);
    const data = {
      name,
      email,
      phone,
      subject: `Stay Inquiry: ${formData.get('stay')}`,
      message: formData.get('message')
    };

    try {
      const res = await fetch(`https://jaladhara.onrender.com/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        showToast('Inquiry Sent Successfully! Our team will reach out to you.');
        e.target.reset();
      }
    } catch (err) {
      console.error('Inquiry failed:', err);
      showToast('Connection failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 px-6 bg-[#022c22] text-white relative overflow-hidden">
      {/* Decorative Background Flourish */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/5 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 md:mb-24">
          <span className="text-emerald-400 font-bold uppercase tracking-[0.4em] text-[10px] md:text-sm block mb-4">Reach the Heart of Wild</span>
          <h2 className="text-4xl md:text-7xl font-bold serif italic tracking-tight mb-6">Plan Your Journey</h2>
          <div className="w-24 md:w-40 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent mx-auto"></div>
        </div>

        <div className="space-y-12">
          {/* Row 1: Info & Form Side-by-Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-stretch">
            {/* Contact Info Card */}
            <div className="bg-white/[0.03] backdrop-blur-xl p-8 md:p-12 rounded-[40px] border border-white/5 flex flex-col justify-between hover:border-emerald-500/30 transition-all duration-500 shadow-2xl">
              <div className="space-y-12">
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-6 border border-emerald-500/20">
                    <i className="fas fa-location-dot text-xl"></i>
                  </div>
                  <h4 className="text-2xl md:text-3xl font-bold mb-4 font-serif italic">Our Base</h4>
                  <p className="text-emerald-100/60 leading-relaxed text-sm md:text-lg font-medium">

                    Boduluru Road, Maredumilli ,<br />
                    Polavaram District, <br />Andhra Pradesh - 533295.
                  </p>
                </div>

                <div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 mb-6 border border-emerald-500/20">
                    <i className="fas fa-phone-volume text-xl"></i>
                  </div>
                  <h4 className="text-2xl md:text-3xl font-bold mb-4 font-serif italic">Get in Touch</h4>
                  <p className="text-emerald-400 font-bold text-3xl md:text-4xl mb-3 tracking-tighter transition-transform hover:scale-105 inline-block cursor-pointer">+91 94407 66328</p>
                  <div className="flex items-center gap-2 text-emerald-100/30 italic text-[11px] md:text-sm font-semibold uppercase tracking-widest">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Available for Reservations 24/7
                  </div>
                </div>
              </div>

              <div className="pt-12">
                <a href="https://wa.me/9440766328" className="group bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-5 rounded-2xl font-bold flex items-center justify-center gap-4 transition-all duration-300 shadow-[0_10px_30px_rgba(16,185,129,0.3)] uppercase text-xs md:text-sm tracking-widest">
                  <i className="fab fa-whatsapp text-2xl group-hover:scale-125 transition-transform"></i> Chat on WhatsApp
                </a>
              </div>
            </div>

            {/* Inquiry Form Card */}
            <div className="bg-emerald-900/40 p-8 md:p-12 rounded-[40px] border border-emerald-500/20 shadow-[0_30px_60px_rgba(0,0,0,0.4)] backdrop-blur-md hover:border-emerald-500/40 transition-all duration-500">
              <h4 className="text-2xl md:text-3xl font-bold mb-8 font-serif italic border-b border-emerald-500/20 pb-4">Send an Inquiry</h4>
              <form id="inquiry-form" className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-[11px] uppercase font-bold text-emerald-300 mb-2 ml-1 tracking-[0.2em] opacity-80">Full Name</label>
                  <input name="name" type="text" required placeholder="Your Name" className="w-full bg-[#022c22]/50 border border-emerald-500/20 rounded-2xl px-5 py-4 text-white placeholder-emerald-900/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] uppercase font-bold text-emerald-300 mb-2 ml-1 tracking-[0.2em] opacity-80">Email Address</label>
                    <input name="email" type="email" required placeholder="email@example.com" className="w-full bg-[#022c22]/50 border border-emerald-500/20 rounded-2xl px-5 py-4 text-white placeholder-emerald-900/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase font-bold text-emerald-300 mb-2 ml-1 tracking-[0.2em] opacity-80">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      maxLength="10"
                      placeholder="10-digit number"
                      onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10)}
                      className="w-full bg-[#022c22]/50 border border-emerald-500/20 rounded-2xl px-5 py-4 text-white placeholder-emerald-900/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase font-bold text-emerald-300 mb-2 ml-1 tracking-[0.2em] opacity-80">Stay Type</label>
                  <select name="stay" className="w-full bg-[#022c22]/50 border border-emerald-500/20 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300 cursor-pointer">
                    <option className="bg-[#022c22]">Hut</option>
                    <option className="bg-[#022c22]">Tent</option>
                    <option className="bg-[#022c22]">Room</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase font-bold text-emerald-300 mb-2 ml-1 tracking-[0.2em] opacity-80">Message</label>
                  <textarea name="message" rows="2" placeholder="Requirements..." className="w-full bg-[#022c22]/50 border border-emerald-500/20 rounded-2xl px-5 py-4 text-white placeholder-emerald-900/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all duration-300"></textarea>
                </div>
                <button type="submit" disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-black py-4 rounded-2xl transition-all duration-300 shadow-xl shadow-emerald-500/20 text-xs tracking-[0.2em] uppercase disabled:opacity-50">
                  {loading ? 'SENDING...' : 'SUBMIT REQUEST'}
                </button>
              </form>
            </div>
          </div>

          {/* Row 2: Wide Map */}
          <div className="relative h-[450px] md:h-[550px] rounded-[40px] overflow-hidden border border-white/5 shadow-2xl hover:border-emerald-500/30 transition-all duration-500">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3803.115844837549!2d81.71251937583693!3d17.597249896791697!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a373b6e31b76fc3%3A0x3e524ed509a9b0!2sJaladhara%20Tourism!5e0!3m2!1sen!2sin!4v1714902868205!5m2!1sen!2sin"
              width="100%"
              height="100%"
              className="grayscale-[0.2] contrast-[1.1] brightness-[0.9]"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Maredumilli Map"
            ></iframe>
            <div className="absolute top-8 right-8">
              <div className="bg-emerald-950/80 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 text-white text-xs font-bold uppercase tracking-widest flex items-center gap-3 shadow-lg">
                <i className="fas fa-map-location-dot text-emerald-400 text-lg"></i> Interactive Forest Location Guide
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
